/**
 * Origem de aquisição (UTM) guardada por 30 dias no navegador.
 * Entra pelo link curto `/d/:id` (que já chega com utm_*) ou por qualquer URL com utm_source.
 * Vai junto nos page views e, depois do login/cadastro, para `POST /auth/me/origem`.
 */

const STORAGE_KEY = "sysigreja:origem-aquisicao";
const VALIDADE_MS = 30 * 24 * 60 * 60 * 1000;

export type OrigemAquisicao = {
  utm_source: string;
  utm_medium?: string;
  utm_campaign?: string;
  grupoId?: number;
  primeiroToqueEm: string;
  expiraEm: string;
  /** Usuários (id) para quem a origem já foi enviada ao backend. */
  reportadaPara?: string[];
};

function parte(value: string | null | undefined): string {
  return (value ?? "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_.-]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 40);
}

/** `youtube/shorts/dev-78` — mesmo formato do backend. */
export function formatarOrigem(
  origem: Pick<OrigemAquisicao, "utm_source" | "utm_medium" | "utm_campaign"> | null,
): string | null {
  const source = parte(origem?.utm_source);
  if (!source) return null;
  return [source, parte(origem?.utm_medium) || "none", parte(origem?.utm_campaign) || "none"].join(
    "/",
  );
}

export function parseGrupoIdDoPath(pathname: string): number | undefined {
  const m = /^\/(?:devocional|d)\/(\d{1,9})\/?$/.exec(pathname);
  return m ? Number(m[1]) : undefined;
}

function ler(): OrigemAquisicao | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as OrigemAquisicao;
    if (!parsed?.utm_source || !parsed.expiraEm) return null;
    if (new Date(parsed.expiraEm).getTime() < Date.now()) {
      window.localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function gravar(origem: OrigemAquisicao) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(origem));
  } catch {
    // modo privado / storage bloqueado: segue sem rastreio
  }
}

export function getOrigemAquisicao(): OrigemAquisicao | null {
  if (typeof window === "undefined") return null;
  return ler();
}

/** Captura utm_* da URL. Mesma origem mantém o primeiro toque; origem nova substitui (último toque). */
export function capturarOrigemDaUrl(
  search: string,
  pathname: string,
  agora: Date = new Date(),
): OrigemAquisicao | null {
  if (typeof window === "undefined") return null;
  const params = new URLSearchParams(search);
  const utm_source = params.get("utm_source")?.trim();
  if (!utm_source) return ler();

  const candidata = {
    utm_source,
    utm_medium: params.get("utm_medium")?.trim() || undefined,
    utm_campaign: params.get("utm_campaign")?.trim() || undefined,
  };
  const atual = ler();
  const grupoId = parseGrupoIdDoPath(pathname) ?? undefined;

  if (atual && formatarOrigem(atual) === formatarOrigem(candidata)) {
    const atualizada = { ...atual, grupoId: grupoId ?? atual.grupoId };
    gravar(atualizada);
    return atualizada;
  }

  const nova: OrigemAquisicao = {
    ...candidata,
    grupoId,
    primeiroToqueEm: agora.toISOString(),
    expiraEm: new Date(agora.getTime() + VALIDADE_MS).toISOString(),
  };
  gravar(nova);
  return nova;
}

export function origemPendenteParaUsuario(userId: string | null | undefined): OrigemAquisicao | null {
  if (!userId) return null;
  const origem = ler();
  if (!origem) return null;
  return origem.reportadaPara?.includes(userId) ? null : origem;
}

export function marcarOrigemReportada(userId: string) {
  const origem = ler();
  if (!origem) return;
  const reportadaPara = Array.from(new Set([...(origem.reportadaPara ?? []), userId])).slice(-10);
  gravar({ ...origem, reportadaPara });
}
